import { AppText, AppIcon } from "@toothfairy/shared-ui";
import AppForms from "@toothfairy/shared-ui/AppForms";
import AppSlider from "@toothfairy/shared-ui/AppSlider";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { View, TouchableOpacity } from "react-native";
import authorisationsHelper from "../../../../../API/Authorisations/authorisationsHelper";
import connectionsHelper from "../../../../../API/Connections/connectionsHelper";
import ToothFairyLoadingPlaceholder from "../../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import Constants from "../../../../../Constants";
import useWorkspaces from "../../../../../Hooks/useWorkspaces";
import { useHistory } from "../../../../../Router";
import QaHiringResourcesLeaf from "./QaHiringResourcesLeaf";
import QaHiringSecretLeaf from "./QaHiringSecretLeaf";
import ToothFairyButton from "../../../../../Components/ToothFairyComponents/ToothFairyButton";

const QaHiringTemplateView = ({
  agentHiringTemplate,
  handleTick,
  excludedResources,
  handleSubAgentTick,
  excludedSubAgents,
  isPlanner,
  hasSubAgents,
}) => {
  const { t } = useTranslation();
  const AppToasty = AppToast.useToast();
  const formikContext = AppForms.useFormikContext();
  const { setFieldValue, values, submitForm, isSubmitting } =
    formikContext || {};
  const [sliderIndex, setSliderIndex] = useState(1);
  const { currentTheme } = AppTheme.useTheme();
  const history = useHistory();
  const { getActiveWorkspace } = useWorkspaces();
  const fields = agentHiringTemplate?.fields;
  const resources = agentHiringTemplate?.resources;
  const [availableAuthorisations, setAvailableAuthorisations] = useState([]);
  const [availableConnections, setAvailableConnections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const handleIndicatorClick = (index) => {
    // Validate Step 1 (Authorisations) before moving to Step 2
    if (index === 2 || (index === 3 && !isPlanner)) {
      if (
        values &&
        Object.values(values).filter(
          (value) => value == null || value == "[object Object]",
        ).length > 0
      ) {
        AppToasty.show(t("settings.qa.fillRequiredFields"), {
          type: "danger",
        });
        return;
      }
    }
    setSliderIndex(index);
  };

  const fetchAuthorisations = async () => {
    const authorisations = await authorisationsHelper.getAuthorisations(
      getActiveWorkspace()?.id,
    );
    const _auth =
      authorisations?.data?.authorisationsByWorkspace?.items?.filter(
        (u) => u?._deleted != true,
      ) || [];
    setAvailableAuthorisations(_auth);
  };

  const fetchConnections = async () => {
    const connections = await connectionsHelper.getDatabaseConnections(
      getActiveWorkspace()?.id,
    );
    const _conn =
      connections?.data?.connectionsByWorkspace?.items?.filter(
        (u) => u?._deleted != true,
      ) || [];
    setAvailableConnections(_conn);
  };

  useEffect(() => {
    try {
      setIsLoading(true);
      if (getActiveWorkspace()?.id) {
        fetchAuthorisations();
        fetchConnections();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }, [getActiveWorkspace()?.id]);

  const renderFormField = (field, isRecap = false) => {
    if (field.type === "authorisation") {
      return (
        <QaHiringSecretLeaf
          options={availableAuthorisations}
          field={field}
          fetchOptions={fetchAuthorisations}
          isRecap={isRecap}
          type={"authorisations/"}
        />
      );
    } else if (field.type === "connection") {
      return (
        <QaHiringSecretLeaf
          options={availableConnections}
          field={field}
          fetchOptions={fetchConnections}
          isRecap={isRecap}
          type={"db_connections/"}
        />
      );
    } else {
      return <></>;
    }
  };

  const getTotalResourceCount = () => {
    return (
      (resources?.dataSources?.length || 0) +
      (resources?.executionEnvironments?.length || 0) +
      (resources?.functions?.length || 0) +
      (resources?.prompts?.length || 0)
    );
  };

  const getSelectedResourceCount = () => {
    const total = getTotalResourceCount();
    return total - excludedResources.length;
  };

  const getSubAgents = () => {
    return resources?.agents || [];
  };

  const getSelectedSubAgentCount = () => {
    const subAgents = getSubAgents();
    return subAgents.length - (excludedSubAgents?.length || 0);
  };

  const baseSliderItems = [
    <View
      style={{
        height: "auto",
        width: 575,
        marginTop: 20,
      }}
    >
      <View
        style={{
          paddingHorizontal: 15,
          paddingVertical: 12,
          backgroundColor: `${currentTheme?.theme?.primary}08`,
          borderRadius: 8,
          marginBottom: 20,
          marginLeft: 10,
          marginRight: 10,
          borderLeftWidth: 3,
          borderLeftColor: currentTheme?.theme?.primary,
        }}
      >
        <AppText
          preventTranslation
          style={{
            fontSize: 14,
            fontWeight: "600",
            marginBottom: 4,
          }}
          color={currentTheme?.theme?.white}
        >
          🔐 {t("settings.wizard.step1ConfigureAuthorisations")}
        </AppText>
        <AppText
          preventTranslation
          style={{
            fontSize: 12,
            lineHeight: 18,
          }}
          color={currentTheme?.theme?.white}
        >
          {t("settings.wizard.step1Description")}
        </AppText>
      </View>

      {fields?.length > 0 ? (
        fields.map((u) => renderFormField(u))
      ) : (
        <View
          style={{
            padding: 20,
            alignItems: "center",
            backgroundColor: `${currentTheme?.theme?.softBackgroundColor}`,
            borderRadius: 8,
            marginLeft: 10,
            marginRight: 10,
            marginBottom: 15,
          }}
        >
          <AppIcon
            name={AppAssets.icons.shield || "shield"}
            size={36}
            color={currentTheme?.theme?.primary}
            style={{ marginBottom: 12 }}
          />
          <AppText
            preventTranslation
            style={{
              fontSize: 14,
              fontWeight: "600",
              marginBottom: 6,
              textAlign: "center",
            }}
            color={currentTheme?.theme?.font_color}
          >
            {t("settings.wizard.noAuthorisationsRequired")}
          </AppText>
          <AppText
            preventTranslation
            style={{
              fontSize: 12,
              textAlign: "center",
              lineHeight: 18,
            }}
            color={currentTheme?.theme?.secondary_font_color}
          >
            {t("settings.wizard.noAuthorisationsRequiredDesc")}
          </AppText>
        </View>
      )}

      <ToothFairyButton
        wrapperStyle={{
          alignSelf: "center",
          marginTop: 10,
        }}
        paddingLeft={10}
        paddingRight={10}
        onPress={() => handleIndicatorClick(2)}
        title={t("settings.wizard.resourceSelection")}
      />
    </View>,

    <View
      style={{
        flexDirection: "column",
        gap: 10,
        padding: 10,
      }}
    >
      <View
        style={{
          paddingHorizontal: 15,
          paddingVertical: 12,
          backgroundColor: `${currentTheme?.theme?.orange}08`,
          borderRadius: 8,
          marginBottom: 10,
          borderLeftWidth: 3,
          borderLeftColor: currentTheme?.theme?.orange,
        }}
      >
        <AppText
          preventTranslation
          style={{
            fontSize: 14,
            fontWeight: "600",
            marginBottom: 4,
          }}
          color={currentTheme?.theme?.orange}
        >
          📦 {t("settings.wizard.step2SelectResources")}
        </AppText>
        <AppText
          preventTranslation
          style={{
            fontSize: 12,
            lineHeight: 18,
          }}
          color={currentTheme?.theme?.secondary_font_color}
        >
          {t("settings.wizard.step2Description")}
        </AppText>

        <View
          style={{
            flexDirection: "row",
            marginTop: 8,
            gap: 15,
          }}
        >
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <View
              style={{
                width: 10,
                height: 10,
                borderRadius: 5,
                backgroundColor: currentTheme?.theme?.primary,
                marginRight: 6,
              }}
            />
            <AppText
              preventTranslation
              style={{
                fontSize: 11,
              }}
              color={currentTheme?.theme?.secondary_font_color}
            >
              {`${getSelectedResourceCount()} ${t("settings.list.selected")}`}
            </AppText>
          </View>

          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <View
              style={{
                width: 10,
                height: 10,
                borderRadius: 5,
                backgroundColor: currentTheme?.theme?.mediumGrey,
                marginRight: 6,
              }}
            />
            <AppText
              preventTranslation
              style={{
                fontSize: 11,
              }}
              color={currentTheme?.theme?.secondary_font_color}
            >
              {excludedResources.length}
            </AppText>
            <AppText
              preventTranslation
              style={{
                fontSize: 11,
                marginLeft: 2,
              }}
              color={currentTheme?.theme?.secondary_font_color}
            >
              excluded
            </AppText>
          </View>
        </View>
      </View>

      {resources?.dataSources?.length > 0 && (
        <>
          <QaHiringResourcesLeaf
            title={"Data sources"}
            iconTitle={"data"}
            resources={resources?.dataSources}
            excludedResources={excludedResources}
            onTick={handleTick}
          />
          <View
            style={{
              height: 1,
              backgroundColor: currentTheme?.theme?.mediumGrey,
              marginVertical: 10,
            }}
          />
        </>
      )}
      {resources?.executionEnvironments?.length > 0 && (
        <>
          <QaHiringResourcesLeaf
            title={"Execution hooks"}
            iconTitle={"data"}
            resources={resources?.executionEnvironments}
            excludedResources={excludedResources}
            onTick={handleTick}
          />
          <View
            style={{
              height: 1,
              backgroundColor: currentTheme?.theme?.mediumGrey,
              marginVertical: 10,
            }}
          />
        </>
      )}
      {resources?.functions?.length > 0 && (
        <QaHiringResourcesLeaf
          title={"Tools"}
          iconTitle={"functions"}
          resources={resources?.functions}
          excludedResources={excludedResources}
          onTick={handleTick}
        />
      )}
      {resources?.prompts?.length > 0 && (
        <>
          <View
            style={{
              height: 1,
              backgroundColor: currentTheme?.theme?.mediumGrey,
              marginVertical: 10,
            }}
          />
          <QaHiringResourcesLeaf
            title={"Prompts"}
            iconTitle={"prompts"}
            resources={resources?.prompts}
            excludedResources={excludedResources}
            onTick={handleTick}
          />
        </>
      )}

      {/* Empty state when no resources exist */}
      {getTotalResourceCount() === 0 && (
        <View
          style={{
            padding: 20,
            alignItems: "center",
            backgroundColor: `${currentTheme?.theme?.softBackgroundColor}`,
            borderRadius: 8,
            marginLeft: 10,
            marginRight: 10,
          }}
        >
          <AppIcon
            name={AppAssets.icons.box || "package"}
            size={36}
            color={currentTheme?.theme?.orange}
            style={{ marginBottom: 12 }}
          />
          <AppText
            preventTranslation
            style={{
              fontSize: 14,
              fontWeight: "600",
              marginBottom: 6,
              textAlign: "center",
            }}
            color={currentTheme?.theme?.font_color}
          >
            {t("settings.wizard.noResourcesToImport")}
          </AppText>
          <AppText
            preventTranslation
            style={{
              fontSize: 12,
              textAlign: "center",
              lineHeight: 18,
            }}
            color={currentTheme?.theme?.secondary_font_color}
          >
            {t("settings.wizard.noResourcesToImportDesc")}
          </AppText>
        </View>
      )}

      {excludedResources?.length > 0 && (
        <View
          style={{
            padding: 12,
            backgroundColor: `${currentTheme?.theme?.orange}10`,
            borderRadius: 6,
            marginLeft: 10,
            marginRight: 10,
            borderLeftWidth: 3,
            borderLeftColor: currentTheme?.theme?.orange,
          }}
        >
          <AppText
            preventTranslation
            style={{
              fontSize: 12,
              fontWeight: "600",
              marginBottom: 4,
            }}
            color={currentTheme?.theme?.orange}
          >
            ⚠️ {t("settings.wizard.someResourcesExcluded")}
          </AppText>
          <AppText
            preventTranslation
            style={{
              fontSize: 11,
              lineHeight: 16,
            }}
            color={currentTheme?.theme?.secondary_font_color}
          >
            Excluded resources won't be available to the agent. You can add them
            later by editing the agent or creating new tools.
          </AppText>
        </View>
      )}

      <ToothFairyButton
        wrapperStyle={{
          alignSelf: "center",
          marginTop: 10,
          minWidth: 170,
        }}
        onPress={() => handleIndicatorClick(isPlanner && hasSubAgents ? 3 : 3)}
        title={
          isPlanner && hasSubAgents
            ? t("settings.wizard.configureAgents")
            : t("settings.wizard.reviewSetup")
        }
      />
    </View>,

    // Step 3: Sub-Agents Selection (only for planners with sub-agents)
    isPlanner && hasSubAgents ? (
      <View
        style={{
          flexDirection: "column",
          gap: 10,
          padding: 10,
        }}
      >
        <View
          style={{
            paddingHorizontal: 15,
            paddingVertical: 12,
            backgroundColor: `${currentTheme?.theme?.purple}08`,
            borderRadius: 8,
            marginBottom: 10,
            borderLeftWidth: 3,
            borderLeftColor: currentTheme?.theme?.purple,
          }}
        >
          <AppText
            preventTranslation
            style={{
              fontSize: 14,
              fontWeight: "600",
              marginBottom: 4,
            }}
            color={currentTheme?.theme?.purple}
          >
            🤖 {t("settings.wizard.step3ConfigureSubAgents")}
          </AppText>
          <AppText
            preventTranslation
            style={{
              fontSize: 12,
              lineHeight: 18,
            }}
            color={currentTheme?.theme?.secondary_font_color}
          >
            {getSubAgents().length > 0
              ? t("settings.wizard.step3DescriptionWithAgents")
              : t("settings.wizard.step3DescriptionNoAgents")}
          </AppText>

          {getSubAgents().length > 0 && (
            <View
              style={{
                flexDirection: "row",
                marginTop: 8,
                gap: 15,
              }}
            >
              <View style={{ flexDirection: "row", alignItems: "center" }}>
                <View
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: currentTheme?.theme?.primary,
                    marginRight: 6,
                  }}
                />
                <AppText
                  preventTranslation
                  style={{
                    fontSize: 11,
                  }}
                  color={currentTheme?.theme?.secondary_font_color}
                >
                  {`${getSelectedSubAgentCount()} ${t(
                    "settings.list.selected",
                  )}`}
                </AppText>
              </View>

              <View style={{ flexDirection: "row", alignItems: "center" }}>
                <View
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: currentTheme?.theme?.mediumGrey,
                    marginRight: 6,
                  }}
                />
                <AppText
                  preventTranslation
                  style={{
                    fontSize: 11,
                  }}
                  color={currentTheme?.theme?.secondary_font_color}
                >
                  {excludedSubAgents?.length || 0}
                </AppText>
                <AppText
                  preventTranslation
                  style={{
                    fontSize: 11,
                    marginLeft: 2,
                  }}
                  color={currentTheme?.theme?.secondary_font_color}
                >
                  {t("settings.wizard.excluded")}
                </AppText>
              </View>
            </View>
          )}
        </View>

        {getSubAgents().length > 0 ? (
          <>
            <QaHiringResourcesLeaf
              title={t("settings.wizard.subAgents")}
              iconTitle={"agents"}
              resources={getSubAgents()}
              excludedResources={excludedSubAgents || []}
              onTick={handleSubAgentTick}
              badgeColor={currentTheme?.theme?.purple}
              badgeText={t("settings.list.orchestrator")}
            />

            {excludedSubAgents?.length > 0 && (
              <View
                style={{
                  padding: 12,
                  backgroundColor: `${currentTheme?.theme?.purple}10`,
                  borderRadius: 6,
                  marginLeft: 10,
                  marginRight: 10,
                  borderLeftWidth: 3,
                  borderLeftColor: currentTheme?.theme?.purple,
                }}
              >
                <AppText
                  preventTranslation
                  style={{
                    fontSize: 12,
                    fontWeight: "600",
                    marginBottom: 4,
                  }}
                  color={currentTheme?.theme?.purple}
                >
                  ⚠️ {t("settings.wizard.someSubAgentsExcluded")}
                </AppText>
                <AppText
                  preventTranslation
                  style={{
                    fontSize: 11,
                    lineHeight: 16,
                  }}
                  color={currentTheme?.theme?.secondary_font_color}
                >
                  {t("settings.wizard.excludedSubAgentsDesc")}
                </AppText>
              </View>
            )}
          </>
        ) : (
          <View
            style={{
              padding: 20,
              alignItems: "center",
              backgroundColor: `${currentTheme?.theme?.softBackgroundColor}`,
              borderRadius: 8,
              marginLeft: 10,
              marginRight: 10,
            }}
          >
            <AppIcon
              name={AppAssets.icons.agent || "robot"}
              size={40}
              color={currentTheme?.theme?.mediumGrey}
              style={{ marginBottom: 15 }}
            />
            <AppText
              preventTranslation
              style={{
                fontSize: 14,
                fontWeight: "600",
                marginBottom: 8,
                textAlign: "center",
              }}
              color={currentTheme?.theme?.font_color}
            >
              {t("settings.wizard.dynamicAgentCreation")}
            </AppText>
            <AppText
              preventTranslation
              style={{
                fontSize: 12,
                textAlign: "center",
                lineHeight: 18,
              }}
              color={currentTheme?.theme?.secondary_font_color}
            >
              {t("settings.wizard.dynamicAgentCreationDesc")}
            </AppText>
          </View>
        )}

        <ToothFairyButton
          wrapperStyle={{
            alignSelf: "center",
            marginTop: 10,
          }}
          onPress={() => handleIndicatorClick(4)}
          title={t("settings.wizard.reviewSetup")}
        />
      </View>
    ) : null,

    <View>
      <View
        style={{
          paddingHorizontal: 15,
          paddingVertical: 12,
          backgroundColor: `${currentTheme?.theme?.primary}08`,
          borderRadius: 8,
          marginBottom: 20,
          marginLeft: 10,
          marginRight: 10,
          borderLeftWidth: 3,
          borderLeftColor: currentTheme?.theme?.primary,
        }}
      >
        <AppText
          preventTranslation
          style={{
            fontSize: 14,
            fontWeight: "600",
            marginBottom: 4,
          }}
          color={currentTheme?.theme?.white}
        >
          {isPlanner && hasSubAgents
            ? t("settings.wizard.step4ReviewConfirm")
            : t("settings.wizard.step3ReviewConfirm")}
        </AppText>
        <AppText
          preventTranslation
          style={{
            fontSize: 12,
            lineHeight: 18,
          }}
          color={currentTheme?.theme?.white}
        >
          {t("settings.wizard.reviewDescription")}
        </AppText>
      </View>

      <View
        style={{
          marginLeft: 10,
          marginBottom: 15,
        }}
      >
        <AppText
          preventTranslation
          style={{
            fontWeight: "bold",
            fontSize: 14,
            marginBottom: 8,
          }}
          color={currentTheme?.theme?.primary}
        >
          🔐 {t("settings.list.authorisations")}
        </AppText>
        {fields?.length > 0 ? (
          fields.map((u) => renderFormField(u, true))
        ) : (
          <View
            style={{
              padding: 12,
              backgroundColor: `${currentTheme?.theme?.softBackgroundColor}`,
              borderRadius: 6,
              marginRight: 10,
            }}
          >
            <AppText
              preventTranslation
              style={{
                fontSize: 12,
                fontStyle: "italic",
              }}
              color={currentTheme?.theme?.secondary_font_color}
            >
              {t("settings.wizard.noAuthorisationsRequiredForAgent")}
            </AppText>
          </View>
        )}
      </View>

      <View
        style={{
          height: 1,
          backgroundColor: currentTheme?.theme?.mediumGrey,
          marginVertical: 10,
        }}
      />

      <View
        style={{
          marginLeft: 10,
          marginBottom: 15,
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            marginBottom: 8,
          }}
        >
          <AppText
            preventTranslation
            style={{
              fontWeight: "bold",
              fontSize: 14,
            }}
            color={currentTheme?.theme?.orange}
          >
            📦
          </AppText>
          <AppText
            preventTranslation
            style={{
              fontWeight: "bold",
              fontSize: 14,
              marginLeft: 4,
            }}
            color={currentTheme?.theme?.orange}
          >
            {t("settings.wizard.resourcesToImport", {
              count: getSelectedResourceCount(),
            })}
          </AppText>
        </View>

        {resources?.dataSources?.length > 0 && (
          <>
            <QaHiringResourcesLeaf
              title={t("settings.wizard.dataSources")}
              iconTitle={"data"}
              resources={resources?.dataSources}
              excludedResources={excludedResources}
              isRecap={true}
            />
            <View
              style={{
                height: 1,
                backgroundColor: currentTheme?.theme?.mediumGrey,
                marginVertical: 10,
              }}
            />
          </>
        )}
        {resources?.executionEnvironments?.length > 0 && (
          <>
            <QaHiringResourcesLeaf
              title={t("settings.wizard.executionHooks")}
              iconTitle={"data"}
              resources={resources?.executionEnvironments}
              excludedResources={excludedResources}
              isRecap={true}
            />
            <View
              style={{
                height: 1,
                backgroundColor: currentTheme?.theme?.mediumGrey,
                marginVertical: 10,
              }}
            />
          </>
        )}
        {resources?.functions?.length > 0 && (
          <QaHiringResourcesLeaf
            title={t("settings.list.toolsLabel")}
            iconTitle={"functions"}
            resources={resources?.functions}
            excludedResources={excludedResources}
            isRecap={true}
          />
        )}
        {resources?.prompts?.length > 0 && (
          <>
            <View
              style={{
                height: 1,
                backgroundColor: currentTheme?.theme?.mediumGrey,
                marginVertical: 10,
              }}
            />
            <QaHiringResourcesLeaf
              title={t("settings.list.prompts")}
              iconTitle={"prompts"}
              resources={resources?.prompts}
              excludedResources={excludedResources}
              isRecap={true}
            />
          </>
        )}

        {/* Empty state when no resources in review */}
        {getTotalResourceCount() === 0 && (
          <View
            style={{
              padding: 12,
              backgroundColor: `${currentTheme?.theme?.softBackgroundColor}`,
              borderRadius: 6,
              marginRight: 10,
            }}
          >
            <AppText
              preventTranslation
              style={{
                fontSize: 12,
                fontStyle: "italic",
              }}
              color={currentTheme?.theme?.secondary_font_color}
            >
              {t("settings.wizard.noAdditionalResourcesRequired")}
            </AppText>
          </View>
        )}

        {/* Sub-Agents Section in Review */}
        {isPlanner && getSubAgents().length > 0 && (
          <>
            <View
              style={{
                height: 1,
                backgroundColor: currentTheme?.theme?.mediumGrey,
                marginVertical: 10,
              }}
            />
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <AppText
                preventTranslation
                style={{
                  fontWeight: "bold",
                  fontSize: 14,
                }}
                color={currentTheme?.theme?.purple}
              >
                🤖
              </AppText>
              <AppText
                preventTranslation
                style={{
                  fontWeight: "bold",
                  fontSize: 14,
                  marginLeft: 4,
                }}
                color={currentTheme?.theme?.purple}
              >
                {t("settings.wizard.subAgentsToImport")} (
              </AppText>
              <AppText
                preventTranslation
                style={{
                  fontWeight: "bold",
                  fontSize: 14,
                }}
                color={currentTheme?.theme?.purple}
              >
                {getSelectedSubAgentCount()}
              </AppText>
              <AppText
                preventTranslation
                style={{
                  fontWeight: "bold",
                  fontSize: 14,
                }}
                color={currentTheme?.theme?.purple}
              >
                {" "}
                {t("settings.wizard.total")})
              </AppText>
            </View>
            <QaHiringResourcesLeaf
              title={t("settings.wizard.subAgents")}
              iconTitle={"agents"}
              resources={getSubAgents()}
              excludedResources={excludedSubAgents || []}
              isRecap={true}
            />
          </>
        )}

        {/* Empty state when nothing to review */}
        {fields?.length === 0 &&
          getTotalResourceCount() === 0 &&
          (!isPlanner || getSubAgents().length === 0) && (
            <View
              style={{
                padding: 20,
                alignItems: "center",
                backgroundColor: `${currentTheme?.theme?.softBackgroundColor}`,
                borderRadius: 8,
                marginLeft: 10,
                marginRight: 10,
                marginTop: 15,
              }}
            >
              <AppIcon
                name={AppAssets.icons.check || "check-circle"}
                size={36}
                color={
                  currentTheme?.theme?.success || currentTheme?.theme?.primary
                }
                style={{ marginBottom: 12 }}
              />
              <AppText
                preventTranslation
                style={{
                  fontSize: 14,
                  fontWeight: "600",
                  marginBottom: 6,
                  textAlign: "center",
                }}
                color={currentTheme?.theme?.font_color}
              >
                {t("settings.wizard.agentReadyToCreate")}
              </AppText>
              <AppText
                preventTranslation
                style={{
                  fontSize: 12,
                  textAlign: "center",
                  lineHeight: 18,
                }}
                color={currentTheme?.theme?.secondary_font_color}
              >
                {t("settings.wizard.agentReadyToCreateDesc")}
              </AppText>
            </View>
          )}
      </View>

      <View
        style={{
          padding: 12,
          backgroundColor: `${currentTheme?.theme?.softBackgroundColor}`,
          borderRadius: 6,
          marginLeft: 10,
          marginRight: 10,
          marginBottom: 15,
        }}
      >
        <AppText
          preventTranslation
          style={{
            fontSize: 11,
            fontWeight: "bold",
          }}
          color={currentTheme?.theme?.primary}
        >
          {t("settings.wizard.afterCreationTip")}
        </AppText>
      </View>

      <ToothFairyButton
        title={"Create Agent"}
        backgroundColor={currentTheme?.theme?.primary}
        fontColor={currentTheme?.theme?.white}
        isDisabled={isSubmitting}
        onPress={submitForm}
        btnContentStyle={{
          paddingLeft: 0,
          paddingRight: 0,
        }}
        style={[
          {
            height: Constants.appButtonHeight,
            width: "auto",
            shadowColor: currentTheme?.theme?.transparent,
            marginHorizontal: 10,
          },
        ]}
        wrapperStyle={[
          {
            margin: 10,
            width: "fit-content",
            minWidth: 150,
            alignSelf: "center",
            height: Constants.appButtonHeight,
          },
        ]}
      />
    </View>,
  ];

  // Filter out null values (conditional steps that don't apply)
  const sliderItems = baseSliderItems.filter(
    (item) => item !== null && item !== undefined,
  );

  if (fields)
    return (
      <>
        {isLoading ? (
          <ToothFairyLoadingPlaceholder caption="messages.loadingConfig" />
        ) : (
          <AppSlider
            onIndicatorClick={handleIndicatorClick}
            isIndicatorComponentClickableToMoveForward={true}
            index={sliderIndex}
            items={sliderItems}
          ></AppSlider>
        )}
      </>
    );
  else
    return (
      <View
        style={{
          padding: 20,
          alignItems: "center",
        }}
      >
        <AppText
          preventTranslation
          style={{
            fontSize: 16,
            fontWeight: "600",
            marginBottom: 10,
          }}
          color={currentTheme?.theme?.font_color}
        >
          {t("settings.wizard.noConfigurationRequired")}
        </AppText>
        <AppText
          preventTranslation
          style={{
            fontSize: 12,
            textAlign: "center",
          }}
          color={currentTheme?.theme?.secondary_font_color}
        >
          {t("settings.wizard.noConfigurationRequiredDesc")}
        </AppText>
        <ToothFairyButton
          title={t("settings.list.createAgent")}
          backgroundColor={currentTheme?.theme?.primary}
          fontColor={currentTheme?.theme?.white}
          isDisabled={isSubmitting}
          onPress={submitForm}
          style={{
            height: Constants.appButtonHeight,
            marginTop: 15,
          }}
          wrapperStyle={{
            width: "fit-content",
            minWidth: 150,
          }}
        />
      </View>
    );
};

export default QaHiringTemplateView;
